import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Save,
  Plus,
  Trash2,
  Eye,
  EyeOff,
  ArrowLeft,
  X,
  BookOpen,
  Lightbulb,
  AlertTriangle,
  Link,
  Hash,
  Clock
} from 'lucide-react'
import { RootState } from '../../store'
import {
  createSummaryCard,
  updateSummaryCard,
  fetchSummaryCardById
} from '../../store/slices/summarySlice'
import { unwrapResult } from '@reduxjs/toolkit'
import { SummaryCardFormData } from '../../types'
import { LGS_SUBJECTS, LGSTopic, getTopicsBySubject, DIFFICULTY_LEVELS } from '../../constants/lgsSubjects'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import TextArea from '../../components/ui/TextArea'
import Badge from '../../components/ui/Badge'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import Modal from '../../components/ui/Modal'
import { formatFractions } from '../../utils/fractionFormatter'

interface ArrayFieldProps {
  title: string
  icon: React.ReactNode
  items: string[]
  onAdd: (item: string) => void
  onRemove: (index: number) => void
  placeholder: string
  description?: string
  allowCommaSplit?: boolean
}

const ArrayField: React.FC<ArrayFieldProps> = ({
  title,
  icon,
  items,
  onAdd,
  onRemove,
  placeholder,
  description,
  allowCommaSplit = false
}) => {
  const [newItem, setNewItem] = useState('')

  const handleAdd = () => {
    const value = newItem.trim()
    if (!value) return

    if (allowCommaSplit) {
      const parts = value
        .split(',')
        .map(v => v.trim())
        .filter(Boolean)
      const uniques = parts.filter(v => !items.includes(v))
      uniques.forEach(v => onAdd(v))
    } else {
      if (!items.includes(value)) {
        onAdd(value)
      }
    }
    setNewItem('')
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center space-x-2">
        {icon}
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
      </div>
      {description && (
        <p className="text-sm text-gray-600 dark:text-gray-400">{description}</p>
      )}
      
      <div className="flex space-x-2">
        <Input
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={placeholder}
          className="flex-1"
        />
        <Button onClick={handleAdd} size="sm">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      
      {items.length > 0 && (
        <div className="space-y-2">
          {items.map((item, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <span className="text-sm text-gray-900 dark:text-white flex-1">{item}</span>
              <Button
                onClick={() => onRemove(index)}
                variant="ghost"
                size="sm"
                className="text-red-500 hover:text-red-700"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const CreateSummaryCard: React.FC = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useSelector((state: RootState) => state.auth)
  const { isLoading, error } = useSelector((state: RootState) => state.summary)
  
  const isEditing = Boolean(id)
  
  const [formData, setFormData] = useState<SummaryCardFormData>({
    title: '',
    subject: '',
    topic: '',
    content: '',
    keyPoints: [],
    formulas: [],
    examples: [],
    importantNotes: [],
    tips: [],
    commonMistakes: [],
    relatedTopics: [],
    difficulty: 'medium',
    estimatedReadTime: 5,
    tags: [],
    color: '#3B82F6',
    isPublished: false
  })
  
  const [showPreview, setShowPreview] = useState(false)
  const [availableTopics, setAvailableTopics] = useState<LGSTopic[]>([])
  const [isSaving, setIsSaving] = useState(false)

  // Load existing card data if editing
  useEffect(() => {
    if (isEditing && id) {
      dispatch(fetchSummaryCardById(id))
        .then(unwrapResult)
        .then((card) => {
          if (card) {
            setFormData({
              title: card.title,
              subject: card.subject,
              topic: card.topic,
              content: card.content,
              keyPoints: card.keyPoints || [],
              formulas: card.formulas || [],
              examples: card.examples || [],
              importantNotes: card.importantNotes || [],
              tips: card.tips || [],
              commonMistakes: card.commonMistakes || [],
              relatedTopics: card.relatedTopics || [],
              difficulty: card.difficulty,
              estimatedReadTime: card.estimatedReadTime,
              tags: card.tags || [],
              color: card.color,
              isPublished: card.isPublished
            })
          }
        })
        .catch((error) => {
          console.error('Error loading summary card:', error)
        })
    }
  }, [dispatch, isEditing, id])

  // Update available topics when subject changes
  useEffect(() => {
    if (formData.subject) {
      const topics = getTopicsBySubject(formData.subject)
      setAvailableTopics(topics)
      
      // Reset topic if it's not available in the new subject
      if (formData.topic && !topics.find(topic => topic.value === formData.topic)) {
        setFormData(prev => ({ ...prev, topic: '' }))
      }
    } else {
      setAvailableTopics([])
    }
  }, [formData.subject])

  const handleInputChange = (field: keyof SummaryCardFormData, value: any) => {
    if (field === 'topic' && value) {
      const selectedTopic = availableTopics.find(topic => topic.value === value)
      if (selectedTopic) {
        setFormData(prev => ({ ...prev, [field]: value, title: selectedTopic.label }))
        return
      }
    }
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleArrayAdd = (field: keyof SummaryCardFormData, item: string) => {
    setFormData(prev => {
      const currentArray = (prev[field] as string[]) || []
      if (currentArray.includes(item)) return prev
      return {
        ...prev,
        [field]: [...currentArray, item]
      } as SummaryCardFormData
    })
  }

  const handleArrayRemove = (field: keyof SummaryCardFormData, index: number) => {
    const currentArray = formData[field] as string[]
    if (currentArray) {
      const newArray = currentArray.filter((_, i) => i !== index)
      handleInputChange(field, newArray)
    }
  }

  const handleTagAdd = (tag: string) => {
    if (tag.trim() && !formData.tags.includes(tag.trim())) {
      handleInputChange('tags', [...formData.tags, tag.trim()])
    }
  }

  const handleTagRemove = (tagToRemove: string) => {
    handleInputChange('tags', formData.tags.filter(tag => tag !== tagToRemove))
  }

  const validateForm = (): boolean => {
    if (!formData.title.trim()) {
      alert('Başlık gereklidir')
      return false
    }
    if (!formData.subject) {
      alert('Ders seçimi gereklidir')
      return false
    }
    if (!formData.topic) {
      alert('Konu seçimi gereklidir')
      return false
    }
    if (!formData.content.trim()) {
      alert('İçerik gereklidir')
      return false
    }
    if (formData.keyPoints.length === 0) {
      alert('En az bir önemli nokta eklemelisiniz')
      return false
    }
    return true
  }

  const handleSave = async () => {
    if (!validateForm() || !user) return

    setIsSaving(true)
    try {
      if (isEditing && id) {
        await dispatch(updateSummaryCard({ id, data: formData }))
        alert('Özet kartı başarıyla güncellendi!')
      } else {
        await dispatch(createSummaryCard({ data: formData, userId: user.id }))
        alert('Özet kartı başarıyla oluşturuldu!')
      }
      navigate('/admin/summary-cards')
    } catch (error) {
      console.error('Error saving summary card:', error)
      alert('Kaydetme sırasında bir hata oluştu')
    } finally {
      setIsSaving(false)
    }
  }

  const estimateReadTime = (content: string, keyPoints: string[], examples: string[]): number => {
    const wordsPerMinute = 200
    const totalWords = (
      content.split(' ').length +
      keyPoints.join(' ').split(' ').length +
      examples.join(' ').split(' ').length
    )
    return Math.max(1, Math.ceil(totalWords / wordsPerMinute))
  }

  // Auto-calculate estimated read time
  useEffect(() => {
    const readTime = estimateReadTime(formData.content, formData.keyPoints, formData.examples)
    if (readTime !== formData.estimatedReadTime) {
      setFormData(prev => ({ ...prev, estimatedReadTime: readTime }))
    }
  }, [formData.content, formData.keyPoints, formData.examples])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-4">
              <Button
                onClick={() => navigate('/admin/summary-cards')}
                variant="ghost"
                size="sm"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Geri
              </Button>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                {isEditing ? 'Özet Kartını Düzenle' : 'Yeni Özet Kartı Oluştur'}
              </h1>
            </div>
            <div className="flex items-center space-x-3">
              <Button
                onClick={() => setShowPreview(!showPreview)}
                variant="outline"
                size="sm"
              >
                {showPreview ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                {showPreview ? 'Düzenleme' : 'Önizleme'}
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                size="sm"
              >
                {isSaving ? (
                  <LoadingSpinner size="sm" className="mr-2" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                {isEditing ? 'Güncelle' : 'Kaydet'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {showPreview ? (
          // Preview Mode
          <Card className="p-8">
            <div className="prose prose-lg max-w-none dark:prose-invert">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-0">
                  {formData.title || 'Başlık Giriniz'}
                </h1>
                <div className="flex items-center space-x-2">
                  <Badge variant={formData.difficulty === 'easy' ? 'success' : formData.difficulty === 'medium' ? 'warning' : 'error'}>
                    {DIFFICULTY_LEVELS.find(d => d.value === formData.difficulty)?.label}
                  </Badge>
                  <div className="flex items-center text-sm text-gray-500">
                    <Clock className="w-4 h-4 mr-1" />
                    {formData.estimatedReadTime} dk
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400">
                  <span><strong>Ders:</strong> {formData.subject || 'Seçiniz'}</span>
                  <span><strong>Konu:</strong> {formData.topic || 'Seçiniz'}</span>
                </div>
              </div>

              {formData.content && (
                <div className="mb-8">
                  <div dangerouslySetInnerHTML={{ __html: formatFractions(formData.content) }} />
                </div>
              )}

              {formData.keyPoints.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-3 flex items-center">
                    <BookOpen className="w-5 h-5 mr-2" />
                    Önemli Noktalar
                  </h3>
                  <ul className="list-disc list-inside space-y-2">
                    {formData.keyPoints.map((point, index) => (
                      <li key={index} className="text-gray-700 dark:text-gray-300">{point}</li>
                    ))}
                  </ul>
                </div>
              )}

              {formData.formulas.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-3">Formüller</h3>
                  <div className="space-y-2">
                    {formData.formulas.map((formula, index) => (
                      <div key={index} className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg font-mono text-center">
                        {formatFractions(formula)}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {formData.examples.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-xl font-semibold mb-3">Örnekler</h3>
                  <div className="space-y-3">
                    {formData.examples.map((example, index) => (
                      <div key={index} className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <div dangerouslySetInnerHTML={{ __html: formatFractions(example) }} />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map((tag, index) => (
                    <Badge key={index} variant="secondary">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </Card>
        ) : (
          // Edit Mode
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Basic Info */}
            <div className="space-y-6">
              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Temel Bilgiler</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {formData.title || 'Başlık *'}
                    </label>
                    <Input
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      placeholder={formData.title || "Özet kartının başlığını giriniz"}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Ders *
                      </label>
                      <select
                        value={formData.subject}
                        onChange={(e) => handleInputChange('subject', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">Ders Seçiniz</option>
                        {LGS_SUBJECTS.map((subject) => (
                          <option key={subject.value} value={subject.value}>
                            {subject.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Konu *
                      </label>
                      <select
                        value={formData.topic}
                        onChange={(e) => handleInputChange('topic', e.target.value)}
                        disabled={!formData.subject}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-50"
                      >
                        <option value="">Konu Seçiniz</option>
                        {availableTopics.map((topic) => (
                          <option key={topic.value} value={topic.value}>
                            {topic.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Zorluk Seviyesi
                      </label>
                      <select
                        value={formData.difficulty}
                        onChange={(e) => handleInputChange('difficulty', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                      >
                        {DIFFICULTY_LEVELS.map((level) => (
                          <option key={level.value} value={level.value}>
                            {level.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Tahmini Okuma Süresi (dk)
                      </label>
                      <Input
                        type="number"
                        value={formData.estimatedReadTime}
                        onChange={(e) => handleInputChange('estimatedReadTime', parseInt(e.target.value) || 1)}
                        min={1}
                        max={60}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Renk
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="color"
                        value={formData.color}
                        onChange={(e) => handleInputChange('color', e.target.value)}
                        className="w-12 h-10 border border-gray-300 rounded-md cursor-pointer"
                      />
                      <Input
                        value={formData.color}
                        onChange={(e) => handleInputChange('color', e.target.value)}
                        placeholder="#3B82F6"
                        className="flex-1"
                      />
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="isPublished"
                      checked={formData.isPublished}
                      onChange={(e) => handleInputChange('isPublished', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <label htmlFor="isPublished" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Yayınla
                    </label>
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">İçerik</h2>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Ana İçerik *
                  </label>
                  <TextArea
                    value={formData.content}
                    onChange={(e) => handleInputChange('content', e.target.value)}
                    placeholder="Özet kartının ana içeriğini Markdown formatında yazınız..."
                    rows={12}
                    className="font-mono text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Markdown formatını kullanabilirsiniz. Kesirler için 1/2 formatını kullanın.
                  </p>
                </div>
              </Card>
            </div>

            {/* Right Column - Arrays */}
            <div className="space-y-6">
              <Card className="p-6">
                <ArrayField
                  title="Önemli Noktalar"
                  icon={<BookOpen className="w-5 h-5 text-blue-500" />}
                  items={formData.keyPoints}
                  onAdd={(item) => handleArrayAdd('keyPoints', item)}
                  onRemove={(index) => handleArrayRemove('keyPoints', index)}
                  placeholder="Önemli bir nokta ekleyin"
                  description="Konunun en önemli noktalarını listeleyin"
                />
              </Card>

              <Card className="p-6">
                <ArrayField
                  title="Formüller"
                  icon={<Hash className="w-5 h-5 text-purple-500" />}
                  items={formData.formulas}
                  onAdd={(item) => handleArrayAdd('formulas', item)}
                  onRemove={(index) => handleArrayRemove('formulas', index)}
                  placeholder="Formül ekleyin (örn: a² + b² = c²)"
                  description="Konuyla ilgili önemli formülleri ekleyin"
                />
              </Card>

              <Card className="p-6">
                <ArrayField
                  title="Örnekler"
                  icon={<Lightbulb className="w-5 h-5 text-yellow-500" />}
                  items={formData.examples}
                  onAdd={(item) => handleArrayAdd('examples', item)}
                  onRemove={(index) => handleArrayRemove('examples', index)}
                  placeholder="Örnek ekleyin"
                  description="Konuyu açıklayan örnekler ekleyin"
                />
              </Card>

              <Card className="p-6">
                <ArrayField
                  title="Önemli Notlar"
                  icon={<AlertTriangle className="w-5 h-5 text-orange-500" />}
                  items={formData.importantNotes}
                  onAdd={(item) => handleArrayAdd('importantNotes', item)}
                  onRemove={(index) => handleArrayRemove('importantNotes', index)}
                  placeholder="Önemli not ekleyin"
                  description="Dikkat edilmesi gereken önemli notlar"
                />
              </Card>

              <Card className="p-6">
                <ArrayField
                  title="İpuçları"
                  icon={<Lightbulb className="w-5 h-5 text-green-500" />}
                  items={formData.tips}
                  onAdd={(item) => handleArrayAdd('tips', item)}
                  onRemove={(index) => handleArrayRemove('tips', index)}
                  placeholder="İpucu ekleyin"
                  description="Konuyu daha iyi anlamak için ipuçları"
                />
              </Card>

              <Card className="p-6">
                <ArrayField
                  title="Sık Yapılan Hatalar"
                  icon={<X className="w-5 h-5 text-red-500" />}
                  items={formData.commonMistakes}
                  onAdd={(item) => handleArrayAdd('commonMistakes', item)}
                  onRemove={(index) => handleArrayRemove('commonMistakes', index)}
                  placeholder="Sık yapılan hata ekleyin"
                  description="Öğrencilerin sık yaptığı hatalar"
                />
              </Card>

              <Card className="p-6">
                <ArrayField
                  title="İlgili Konular"
                  icon={<Link className="w-5 h-5 text-indigo-500" />}
                  items={formData.relatedTopics}
                  onAdd={(item) => handleArrayAdd('relatedTopics', item)}
                  onRemove={(index) => handleArrayRemove('relatedTopics', index)}
                  placeholder="İlgili konu ekleyin"
                  description="Bu konuyla bağlantılı diğer konular"
                />
              </Card>

              <Card className="p-6">
                <ArrayField
                  title="Etiketler"
                  icon={<Hash className="w-5 h-5 text-blue-500" />}
                  items={formData.tags}
                  onAdd={(item) => handleArrayAdd('tags', item)}
                  onRemove={(index) => handleArrayRemove('tags', index)}
                  placeholder="Etiket ekleyin"
                  description="Arama ve filtreleme için etiketler ekleyin"
                  allowCommaSplit={true}
                />
              </Card>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default CreateSummaryCard